import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import {
  Estate_UpdateEstateInput,
  EstateInputDataFormatDataType,
  EstateInputDataFormatType,
  EstateInputDataInstance_FieldsFragment,
  EstateType,
  EstateWorkItem,
  EstateWorkItem_GetEstateWorkItemsByPortalIdDocument,
  EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
  useEstate_UpdateEstateAndEstateInputDataInstancesMutation,
} from 'queries';
import * as yup from 'yup';

import { getEstateUpdateEstateInputData } from 'utils/estate';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import AssignedTaskMatterInfoRequiredInfoForm from './assigned-task-matter-info-required-info-form';
import AssignedTaskMatterInfoTruadminForm from './assigned-task-matter-info-truadmin-form';
import AssignedTaskMatterInfoTruplanningForm from './assigned-task-matter-info-truplanning-form';
import AssignedTaskMatterInfoTruproxyForm from './assigned-task-matter-info-truproxy-form';
import AssignedTaskMatterInfoTrustadminForm from './assigned-task-matter-info-trustadmin-form';

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
    color: '#1C2541',
    fontSize: '14px',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
  },
  headerIcons: {
    marginRight: '8px',
  },
  columnHeader: {
    width: '200px',
  },
  column: {
    display: 'flex',
    width: '100%',
    padding: '20px 0',
    borderBottom: '1px solid #ECEEF1',
    alignItems: 'center',
  },
  columnBody: {
    display: 'flex',
    flex: 1,
  },
  bodyText: {
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    fontSize: '14px',
    lineHeight: '22px',
  },
  lastUpdatedText: {
    color: '#596674',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  actionContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '40px',
    gap: '20px',
    marginBottom: '200px',
    '& > button': {
      borderRadius: '100px',
      padding: '11px, 32px, 11px, 32px',
    },
  },
  cancelButton: {
    color: '#1C2541',
    backgroundColor: '#FFFFFF',
  },
}));

const schema = yup.object().shape({
  type: yup.string().required(),
  estateTruadminFields: yup.object().when('type', {
    is: EstateType.Truadmin,
    then: (schema) =>
      schema.shape({
        decedentDateOfBirth: yup.string().required('Required'),
        decedentDateOfDeath: yup.string().required('Required'),
        decedentFirstName: yup.string().required('Required'),
        decedentLastAddressStreet: yup.string().required('Required'),
        decedentLastAddressCity: yup.string().required('Required'),
        decedentLastAddressZip: yup.string().required('Required'),
        decedentLastAddressState: yup.string().required('Required'),
        decedentLastName: yup.string().required('Required'),
        decedentSsn: yup.string().required('Required'),
        clientPhoneNumber: yup.string().nullable(),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  estateTruproxyFields: yup.object().when('type', {
    is: EstateType.Truproxy,
    then: (schema) =>
      schema.shape({
        wardDateOfBirth: yup.string().required('Required'),
        wardFirstName: yup.string().required('Required'),
        wardLastName: yup.string().required('Required'),
        wardAddressCity: yup.string().required('Required'),
        wardAddressState: yup.string().required('Required'),
        wardAddressStreet: yup.string().required('Required'),
        wardAddressZip: yup.string().required('Required'),
        clientPhoneNumber: yup.string().nullable(),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  estateTruplanningFields: yup.object().when('type', {
    is: EstateType.Truplanning,
    then: (schema) =>
      schema.shape({
        clientDateOfBirth: yup.string().required('Required'),
        clientAddressCity: yup.string().required('Required'),
        clientAddressZip: yup.string().required('Required'),
        clientAddressState: yup.string().required('Required'),
        clientAddressStreet: yup.string().required('Required'),
        clientPhoneNumber: yup.string().nullable(),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  estateTrustadminFields: yup.object().when('type', {
    is: EstateType.Trustadmin,
    then: (schema) =>
      schema.shape({
        trustName: yup.string().required('Required'),
        estateGrantors: yup
          .array()
          .min(1, 'At least one grantor is required')
          .of(
            yup.object().shape({
              grantorFirstName: yup.string().required('Required'),
              grantorLastName: yup.string().required('Required'),
            }),
          ),
        estateTrustees: yup
          .array()
          .min(1, 'At least one trustee is required')
          .of(
            yup.object().shape({
              trusteeFirstName: yup.string().required('Required'),
              trusteeLastName: yup.string().required('Required'),
              isPrimaryTrustee: yup.boolean().required('Required'),
              trusteePhoneNumber: yup.string().when('isPrimaryTrustee', {
                is: true,
                then: (schema) => schema.required('Please enter a phone number'),
                otherwise: (schema) => schema.nullable(),
              }),
              trusteeEmail: yup
                .string()
                .email('Enter a valid email')
                .when('isPrimaryTrustee', {
                  is: true,
                  then: (schema) => schema.required('Required'),
                  otherwise: (schema) => schema.nullable(),
                }),
            }),
          )
          .test(
            'estateTrustees',
            () => 'Select a primary point of contact',
            (trustees) => {
              const primaryTrustees = trustees?.find((trustee) => trustee.isPrimaryTrustee);
              return Boolean(primaryTrustees);
            },
          )
          .test(
            'estateTrustees',
            () => 'Select only one primary point of contact',
            (trustees) => {
              const primaryTrustees = trustees?.filter((trustee) => trustee.isPrimaryTrustee) || [];
              return primaryTrustees.length === 1;
            },
          ),
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  infoEstateInputDataInstanceFormValues: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      data: yup.string(),
      dataType: yup.string<EstateInputDataFormatDataType>(),
    }),
  ),
});

type Props = {
  estateWorkItem: EstateWorkItem;
  estateInputDataInstances: EstateInputDataInstance_FieldsFragment[];
  refetchEstateInputDataInstances: Function;
  onClose: () => void;
};

export type InfoEstateInputDataInstanceFormValue = {
  id: string;
  data?: string;
};

export type AssignedTaskMatterInfoType = Partial<Estate_UpdateEstateInput> & {
  type: EstateType;
  infoEstateInputDataInstanceFormValues: InfoEstateInputDataInstanceFormValue[];
};

const AssignedTaskMatterInfo = ({
  estateWorkItem,
  estateInputDataInstances,
  refetchEstateInputDataInstances,
  onClose,
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const [updateEstateAndEstateInputDataInstances] =
    useEstate_UpdateEstateAndEstateInputDataInstancesMutation({
      refetchQueries: [
        {
          query: EstateWorkItem_GetEstateWorkItemsByPortalIdDocument,
          variables: { portalId },
        },
        {
          query: EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
          variables: { portalId },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Matter information updated successfully',
          severity: NotificationSeverity.SUCCESS,
        });
        refetchEstateInputDataInstances();
      },
      onError: (e) => {
        showAlert({
          message: 'Error updating estate and estate input data instances: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const estate = estateWorkItem.estate!;
  const infoEstateInputDataInstances = sort(
    estateInputDataInstances.filter(
      (estateInputDataInstance) =>
        estateInputDataInstance.estateInputDataFormat!.type === EstateInputDataFormatType.Data,
    ),
  ).asc((instance) => instance.estateInputDataFormat.name);

  const infoEstateInputDataInstanceFormValues = infoEstateInputDataInstances.map(
    (estateInputDataInstance) => {
      return {
        id: estateInputDataInstance.id,
        data: estateInputDataInstance.data || '',
        dataType: estateInputDataInstance.estateInputDataFormat.dataType,
      } as InfoEstateInputDataInstanceFormValue;
    },
  );

  const defaultValues = getEstateUpdateEstateInputData(estate);

  const formMethods = useForm<AssignedTaskMatterInfoType>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: estateWorkItem.documentGenerationTemplateId ? undefined : yupResolver(schema),
    values: {
      ...defaultValues,
      type: estate.type,
      infoEstateInputDataInstanceFormValues,
    },
  });

  const submitForm = async (formData: AssignedTaskMatterInfoType) => {
    const updateEstateInputData = {
      ...formData,
    } as any;
    delete updateEstateInputData.infoEstateInputDataInstanceFormValues;
    delete updateEstateInputData.type;

    if (updateEstateInputData.estateTruplanningFields?.clientPhoneNumber === '+1') {
      updateEstateInputData.estateTruplanningFields.clientPhoneNumber = undefined;
    }

    if (updateEstateInputData.estateTruadminFields?.clientPhoneNumber === '+1') {
      updateEstateInputData.estateTruadminFields.clientPhoneNumber = undefined;
    }

    if (updateEstateInputData.estateTruproxyFields?.clientPhoneNumber === '+1') {
      updateEstateInputData.estateTruproxyFields.clientPhoneNumber = undefined;
    }

    const infoEstateInputDataInstanceFormValues =
      formData.infoEstateInputDataInstanceFormValues || [];
    const updateEstateInputDataInstanceInputs = infoEstateInputDataInstanceFormValues.map(
      (infoEstateInputDataInstanceFormValue) => {
        return {
          portalId: estate.portalId,
          id: infoEstateInputDataInstanceFormValue.id,
          data: infoEstateInputDataInstanceFormValue.data,
        };
      },
    );

    updateEstateAndEstateInputDataInstances({
      variables: {
        input: {
          portalId: estate.portalId,
          updateEstateInput: {
            id: estate.id,
            portalId: estate.portalId,
            ...updateEstateInputData,
          },
          updateEstateInputDataInstancesInput: {
            portalId: estate.portalId,
            updateEstateInputDataInstanceInputs: updateEstateInputDataInstanceInputs,
          },
          estateWorkItemId: estateWorkItem.id,
        },
      },
    });
  };

  const renderEstateTypeForm = () => {
    switch (estate.type) {
      case EstateType.Truadmin:
        return <AssignedTaskMatterInfoTruadminForm />;
      case EstateType.Truplanning:
        return <AssignedTaskMatterInfoTruplanningForm />;
      case EstateType.Truproxy:
        return <AssignedTaskMatterInfoTruproxyForm />;
      case EstateType.Trustadmin:
        return <AssignedTaskMatterInfoTrustadminForm />;
      default:
        return null;
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(submitForm)}>
          <div className={classes.container}>
            {!estateWorkItem.documentGenerationTemplateId && renderEstateTypeForm()}
            {infoEstateInputDataInstances.length > 0 && (
              <AssignedTaskMatterInfoRequiredInfoForm
                estateInputDataInstances={infoEstateInputDataInstances}
              />
            )}
            <div className={classes.actionContainer}>
              <Button
                variant="contained"
                size="small"
                className={classes.cancelButton}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={formMethods.formState.isSubmitting}
              >
                Save changes
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default AssignedTaskMatterInfo;
